import React, { Dispatch, SetStateAction } from "react";
import Select, { components } from "react-select";
import styled from "styled-components";
import dropDownArrowUrl from "../../assets/dropDown/arrow_drop_down_black_24.png";
import useIsMobile from "../../hooks/useIsMobile";

interface DropDownProps {
  boxWidth?: number;
  initText?: string;
  optionList?: OptionType[];
  isDisabled?: boolean;
  setDropDownValue?: Dispatch<SetStateAction<any>>;
  dropdownValue?: OptionType;
  changeEvent?: () => void;
  isInModal?: boolean;
  isMulti?: boolean;
  setDropDownMultiValue?: Dispatch<SetStateAction<any[]>>;
  dropdownMultiValue?: OptionType[];
  initValue?: string;
}

interface SDropDownBoxContainer {
  isDisabled?: boolean;
}

interface SDropDownBox {
  boxWidth?: number;
}

export interface OptionType {
  value?: string;
  label?: string;
  isDisabled?: boolean;
}

export const defaultOptions: OptionType[] = [
  { value: "uid", label: "UID" },
  { value: "name", label: "이름" },
  { value: "nickname", label: "닉네임" },
  { value: "phone", label: "전화번호" },
  { value: "mail", label: "뉴 링키드메일" },
];

export default function DropDown({
  optionList,
  boxWidth,
  initText,
  isDisabled,
  setDropDownValue,
  dropdownValue,
  changeEvent,
  isInModal,
  isMulti = false,
  dropdownMultiValue,
  setDropDownMultiValue,
  initValue,
}: DropDownProps) {
  const isMobile = useIsMobile();

  const handleChange = (
    // option: ValueType<OptionType | OptionType[], false>
    option: any
  ) => {
    if (changeEvent) {
      changeEvent();
    }

    if (setDropDownValue) {
      setDropDownValue(
        option ?? {
          value: "",
          label: "선택",
        }
      );
    }
  };

  const handleMultiChange = (
    // option: ValueType<OptionType | OptionType[], true>
    option: any
  ) => {
    if (changeEvent) {
      changeEvent();
    }

    if (setDropDownMultiValue) {
      setDropDownMultiValue(option);
    }
  };

  const customStyles = {
    container: (base: any) => ({
      ...base,
      width: "auto",
      minWidth: boxWidth ? `${boxWidth}px` : "220px",
      pointerEvents: isDisabled ? "not-allowed" : null, // cancel mouse events when disabled
      backgroundColor: "#f6f6f8",
    }),
    placeholder: (base: any) => ({
      ...base,
      fontSize: 14,
      textAlign: "left",
      color: "#858c97",
      margin: 0,
    }),
    control: (base: any, state: any) => ({
      ...base,
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      width: "auto",
      minWidth: boxWidth ? `${boxWidth}px` : "220px",
      borderRadius: 3,
      borderBottomLeftRadius: state.isFocused ? 0 : 3,
      borderBottomRightRadius: state.isFocused ? 0 : 3,
      border: `1px solid ${
        isInModal && state.isDisabled ? "transparent" : "#d4d9e1"
      }`,
      boxShadow: state.isFocused ? "0 3px 12px 0 rgba(0, 0, 0, 0.1)" : "none",
      zIndex: 1,
      "&:hover": {
        border: "1px solid #d4d9e1",
      },
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      padding: "0px 8px 0px 0px",
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: 0,
      paddingLeft: 16,
    }),
    indicatorSeparator: (base: any) => ({
      ...base,
      display: "none",
    }),
    singleValue: (base: any) => ({
      ...base,
      fontSize: 14,
      textAlign: "left",
      color: dropdownValue?.value === "" ? "#858c97" : "#333333",
      margin: 0,
    }),
    menuPortal: (base: any) => ({
      ...base,
      zIndex: 9999,
    }),
    menu: (base: any) => ({
      ...base,
      top: -10,
      padding: 0,
      borderRadius: 0,
      borderTop: "none",
    }),
    option: (base: any, state: any) => ({
      ...base,
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      backgroundColor: "#ffffff",
      fontSize: 14,
      textAlign: "left",
      paddingLeft: 16,
      color: state.isDisabled ? "#aaaaaa" : "#505050",
      "&:hover": {
        backgroundColor: state.isDisabled ? "#ffffff" : "#f5f5f5",
      },
    }),
    menuList: (base: any) => ({
      ...base,
      padding: 0,
    }),
  };

  const DropdownIndicator = (props: any) => {
    return (
      <components.DropdownIndicator {...props}>
        <img
          src={dropDownArrowUrl}
          style={{ width: 24, height: 24 }}
          alt="arrow"
        />
      </components.DropdownIndicator>
    );
  };

  return (
    <>
      {isMobile ? (
        <DropDownBoxContainer isDisabled={isDisabled}>
          <DropDownBox
            boxWidth={boxWidth}
            value={dropdownValue?.value}
            disabled={isDisabled}
            multiple={isMulti}
            onChange={(e) => {
              if (changeEvent) {
                changeEvent();
              }

              if (setDropDownMultiValue) {
                const options = e.target.options;
                let value = [];

                for (let i = 0, l = options.length; i < l; i++) {
                  if (options[i].selected) {
                    value.push({
                      value:
                        options[i].value === "전체"
                          ? initValue ?? options[i].value
                          : options[i].value,
                      option: options[i].value,
                    });
                  }
                }

                setDropDownMultiValue(value);
              }

              console.log(e.target.value);

              if (setDropDownValue) {
                setDropDownValue({
                  value:
                    e.target.value === "전체"
                      ? initValue ?? e.target.value
                      : e.target.value,
                  label: e.target.selectedOptions[0].label ?? e.target.value,
                });
              }
            }}
          >
            <DropDownOption defaultValue={initText ?? "선택"}>
              {initText ?? "선택"}
            </DropDownOption>
            {(optionList ?? defaultOptions)?.map((l, idx) => (
              <DropDownOption
                value={l?.value}
                label={l?.label}
                key={idx}
                disabled={l?.isDisabled ? true : false}
              >
                {l?.label}
              </DropDownOption>
            ))}
          </DropDownBox>
        </DropDownBoxContainer>
      ) : (
        <Select
          onChange={(option) => {
            if (isMulti) {
              handleMultiChange(option);
            } else {
              handleChange(option);
            }
          }}
          options={optionList ?? defaultOptions}
          placeholder={initText ?? "선택"}
          styles={customStyles}
          components={{ DropdownIndicator }}
          isDisabled={isDisabled}
          isSearchable={false}
          value={isMulti ? dropdownMultiValue : dropdownValue}
          menuPortalTarget={document.body}
          menuPosition={"fixed"}
          isOptionDisabled={(option) => (option.isDisabled ? true : false)}
          isMulti={isMulti}
        />
      )}
    </>
  );
}

export const DropDownBoxContainer = styled.div<SDropDownBoxContainer>`
  background-color: ${({ isDisabled }) => (isDisabled ? "#f5f5f5" : "#ffffff")};
`;

export const DropDownBox = styled.select<SDropDownBox>`
  width: ${({ boxWidth }) => boxWidth ?? 180}px;
  height: 38px;
  padding: 7px 8px 7px 16px;

  background: url(${dropDownArrowUrl}) no-repeat 95% 50%;
  background-size: 24px;
  border: solid 1px ${({ theme }) => theme.buttonColor.lineNormal};
  border-radius: 3px;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  outline: none;
  writing-mode: horizontal-tb !important;

  font-size: 14px;
  text-align: left;
  color: #858c97;

  option:hover {
    background-color: #f5f5f5;
  }

  &:disabled {
    color: "#aaafb9";
  }
`;

export const DropDownOption = styled.option`
  font-size: 14px;
  text-align: left;
  color: #505050;
  background-color: #ffffff;
  padding: 10px;
  height: 38px;

  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  &:hover {
    background-color: #f5f5f5;
  }
`;
